import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Box, Typography, Button, CircularProgress, Alert, Dialog, DialogTitle,
    DialogContent, DialogActions, Backdrop, IconButton, Stack, Fade, useTheme
} from '@mui/material';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import ErrorRoundedIcon from '@mui/icons-material/ErrorRounded';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import HelpOutlineRoundedIcon from '@mui/icons-material/HelpOutlineRounded';
import LockRoundedIcon from '@mui/icons-material/LockRounded';
import useDataFetch, { usePostDataFetch } from '../../utils/useDataFetch';

import Pitch from '../../components/lineup/Pitch';
import PlayerSelectModal from '../../components/lineup/PlayerSelectModal';
import LineupControls from '../../components/lineup/LineupControls';

const LineupBuilder = () => {
    const { teamId, terminId } = useParams();
    const navigate = useNavigate();
    const theme = useTheme();

    const { data: formationData, isLoading: formationLoading } =
        useDataFetch({ url: `/lineups/team/${teamId}/formations` });
    const { data: lineupData, isLoading: lineupLoading, refetch: refetchLineup } =
        useDataFetch({ url: `/lineups/termin/${terminId || 0}` });
    const { data: statsData } = useDataFetch({ url: `/lineups/team/${teamId}/stats` });
    const { data: detailData, isLoading: detailLoading } = useDataFetch({
        url: terminId ? `/termine/${terminId}/details?teamId=${teamId}` : null
    });
    const { data: gastData } = useDataFetch({ url: `/teams/${teamId}/gastspieler` });
    const { data: teamData } = useDataFetch({ url: `/teams/info/${teamId}` });
    const { data: previousLineups } = useDataFetch({ url: `/lineups/team/${teamId}/previous` });

    const { data: lineUpData, error: lineUpError, postData: saveLineup } = usePostDataFetch();

    const [selectedFormationId, setSelectedFormationId] = useState('');
    const [playersOnPitch, setPlayersOnPitch] = useState({});
    const [isSaving, setIsSaving] = useState(false);
    const [showToast, setShowToast] = useState({ visible: false, type: '', message: '' });
    const [copyDialogOpen, setCopyDialogOpen] = useState(false);
    const [isDirty, setIsDirty] = useState(false);
    const [isFinalSaving, setIsFinalSaving] = useState(false);
    const [isManual, setIsManual] = useState(false);

    // UI Modals
    const [selectedPosForModal, setSelectedPosForModal] = useState(null);
    const [helpDialogOpen, setHelpDialogOpen] = useState(false);

    const saveTimer = useRef(null);

    const formations = formationData?.formations ?? [];
    const activeStats = statsData?.stats ?? {};

    // Team-Approval Check
    const isTeamApproved = teamData?.team?.status === 'approved';

    // Aufstellung laden
    useEffect(() => {
        if (lineupData?.lineup) {
            setSelectedFormationId(lineupData.lineup.formation_id);
            const loaded = {};
            lineupData.lineup.eintraege.forEach(e => {
                if (e.pos_id) loaded[e.pos_id] = e.user_id ?? `g_${e.gastspieler_id}`;
            });
            setPlayersOnPitch(loaded);
        } else if (formations.length > 0 && !selectedFormationId) {
            const def = formations.find(f => f.name === '4-2-3-1') ?? formations[0];
            if (def) setSelectedFormationId(def.id);
        }
    }, [lineupData, formations]);

    const showToastMessage = (type, message) => {
        setShowToast({ visible: true, type, message });
        setTimeout(() => setShowToast({ visible: false, type: '', message: '' }), 4000);
    };

    const handleSave = useCallback(async () => {
        if (!selectedFormationId || lineupData?.lineup?.is_locked) return;

        setIsSaving(true);
        if (isManual) setIsFinalSaving(true);

        const eintraege = Object.entries(playersOnPitch).map(([posId, val]) => {
            const isGast = typeof val === 'string' && val.startsWith('g_');
            return {
                pos_id: parseInt(posId),
                user_id: isGast ? null : val,
                gastspieler_id: isGast ? parseInt(val.replace('g_', '')) : null,
                ist_ersatz: false
            };
        });

        try {
            saveLineup({
                url: `/lineups/termin/${terminId}`,
                aBody: {
                    team_id: teamId,
                    formation_id: selectedFormationId,
                    status: isManual ? 'VEROEFFENTLICHT' : 'ENTWURF',
                    eintraege
                },
                skipAuth: false
            });
        } catch (err) {
            showToastMessage('error', `❌ Fehler beim Speichern: ${err.message}`);
        } finally {
            setIsSaving(false);
            if (isManual) setIsFinalSaving(false);
        }
    }, [selectedFormationId, playersOnPitch, teamId, terminId, lineupData, saveLineup]);

    useEffect(() => {
        if (lineUpData) {
            if (lineUpData?.success) {
                if (isManual) {
                    showToastMessage('success', '✅ Aufstellung veröffentlicht');
                    refetchLineup();
                }
            } else {
                showToastMessage('error', `❌ Fehler: ${lineUpData?.error || 'Unbekannter Fehler'}`);
            }
        }
    }, [lineUpData]);

    // Autosave (debounced, 2 s)
    useEffect(() => {
        if (!isDirty || Object.keys(playersOnPitch).length === 0) return;
        clearTimeout(saveTimer.current);
        saveTimer.current = setTimeout(() => {
            handleSave();
            setIsManual(false);
            setIsDirty(false);
        }, 2000);
        return () => clearTimeout(saveTimer.current);
    }, [playersOnPitch, selectedFormationId, handleSave, isDirty]);

    const handleCopyPreviousLineup = async (prevLineupId) => {
        try {
            const response = await fetch(`/lineups/lineup/${prevLineupId}`, {
                headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
            });
            const result = await response.json();

            if (result.success && result.lineup) {
                const loaded = {};
                result.lineup.eintraege.forEach(e => {
                    if (e.pos_id) loaded[e.pos_id] = e.user_id ?? `g_${e.gastspieler_id}`;
                });
                setPlayersOnPitch(loaded);
                setSelectedFormationId(result.lineup.formation_id);
                setCopyDialogOpen(false);
                setIsDirty(true);
                showToastMessage('success', 'Aufstellung kopiert');
            }
        } catch (err) {
            showToastMessage('error', `❌ Fehler: ${err.message}`);
        }
    };

    const handleClearLineup = () => {
        setPlayersOnPitch({});
        setIsDirty(true);
        showToastMessage('success', 'Aufstellung geleert');
    };

    const handlePlayerSelect = (playerId) => {
        if (!selectedPosForModal) return;

        setPlayersOnPitch(prev => {
            const newPlayers = { ...prev };
            if (playerId !== '') {
                Object.keys(newPlayers).forEach(posId => {
                    if (newPlayers[posId] === playerId) {
                        newPlayers[posId] = '';
                    }
                });
            }
            newPlayers[selectedPosForModal.id] = playerId;
            return newPlayers;
        });

        setIsDirty(true);
        setSelectedPosForModal(null);
    };

    if (formationLoading || lineupLoading || detailLoading)
        return <CircularProgress />;

    if (!isTeamApproved) {
        return (
            <Box sx={{ p: 5, textAlign: 'center' }}>
                <Alert severity="warning" sx={{ mb: 3, borderRadius: "12px", fontWeight: 700 }}>
                    ⏳ Dein Team ist noch nicht genehmigt.
                </Alert>
                <Button onClick={() => navigate(-1)} variant="outlined" sx={{ borderRadius: "12px" }}>Zurück</Button>
            </Box>
        );
    }

    const appointment = detailData?.appointment;
    if (appointment?.typ !== 'Spiel') {
        return (
            <Box sx={{ p: 5, textAlign: 'center' }}>
                <Alert severity="warning" sx={{ borderRadius: "12px", fontWeight: 700 }}>
                    Aufstellungen sind nur für Termine der Kategorie „Spiel" verfügbar.
                </Alert>
                <Button onClick={() => navigate(-1)} sx={{ mt: 2, borderRadius: "12px" }} variant="outlined">Zurück</Button>
            </Box>
        );
    }

    const isLocked = lineupData?.lineup?.is_locked;
    const activeFormation = formations.find(f => f.id == selectedFormationId);

    const availablePlayers = [
        ...(detailData?.groups?.zusagen ?? [])
            .filter(p => !p.preferred_positions?.includes('Trainer')),
        ...(gastData?.gastspieler ?? [])
            .map(g => ({
                id: `g_${g.id}`,
                first_name: g.name,
                last_name: '',
                isGast: true,
                preferred_positions: []
            }))
    ];

    const ToastAlert = () => {
        if (!showToast.visible) return null;
        const isSuccess = showToast.type === 'success';
        return (
            <Alert
                icon={isSuccess ? <CheckCircleRoundedIcon /> : <ErrorRoundedIcon />}
                severity={isSuccess ? 'success' : 'error'}
                sx={{
                    position: 'fixed',
                    bottom: 24,
                    right: 24,
                    zIndex: 10000,
                    borderRadius: "12px",
                    fontWeight: 800,
                    boxShadow: '0 10px 40px rgba(0,0,0,0.5)',
                    border: '1px solid',
                    borderColor: isSuccess ? 'primary.main' : 'error.main',
                    bgcolor: isSuccess ? 'rgba(190, 242, 100, 0.1)' : 'rgba(211, 47, 47, 0.1)',
                    backdropFilter: 'blur(10px)',
                    animation: 'slideIn 0.3s ease-in',
                    '@keyframes slideIn': {
                        from: { transform: 'translateX(400px)', opacity: 0 },
                        to: { transform: 'translateX(0)', opacity: 1 }
                    }
                }}
            >
                {showToast.message}
            </Alert>
        );
    };

    return (
        <Fade in={true} timeout={800}>
            <Box sx={{ p: { xs: 2, md: 4 }, maxWidth: 1000, mx: 'auto', mb: 10 }}>
                <Backdrop
                    sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1, display: 'flex', flexDirection: 'column', gap: 2, bgcolor: 'rgba(0,0,0,0.85)', backdropFilter: 'blur(10px)' }}
                    open={isFinalSaving}
                >
                    <CircularProgress color="primary" thickness={5} size={60} />
                    <Typography variant="h5" sx={{ fontWeight: 900 }}>Wird veröffentlicht...</Typography>
                </Backdrop>

                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', mb: 5, flexWrap: 'wrap', gap: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <IconButton onClick={() => navigate(-1)} sx={{ bgcolor: 'rgba(255,255,255,0.05)', borderRadius: 3, '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' } }}>
                            <ArrowBackRoundedIcon />
                        </IconButton>
                        {/* <Box>
                            <Typography variant="h3" sx={{ fontWeight: 900, mb: 0.5, textTransform: 'uppercase', letterSpacing: '-0.02em' }}>
                                Taktik <span style={{ color: theme.palette.primary.main }}>Board</span>
                            </Typography>
                            <Typography variant="h6" sx={{ color: 'text.secondary', fontWeight: 600, opacity: 0.8 }}>
                                {appointment.titel}
                            </Typography>
                        </Box> */}
                    </Box>
                    <Stack direction="row" spacing={2} alignItems="center">
                        {isSaving && !isFinalSaving && (
                            <Typography variant="caption" sx={{ color: 'primary.main', fontWeight: 900, textTransform: 'uppercase', letterSpacing: '0.1em' }}>
                                Autospeichern…
                            </Typography>
                        )}
                        <IconButton onClick={() => setHelpDialogOpen(true)} sx={{ color: 'primary.main', bgcolor: 'rgba(190, 242, 100, 0.1)', borderRadius: 3 }}>
                            <HelpOutlineRoundedIcon />
                        </IconButton>
                    </Stack>
                </Box>

                {isLocked && (
                    <Alert
                        severity="info"
                        icon={<LockRoundedIcon />}
                        sx={{
                            mb: 4,
                            borderRadius: 4,
                            fontWeight: 800,
                            border: '1px solid',
                            borderColor: 'info.main',
                            bgcolor: 'rgba(2, 136, 209, 0.05)'
                        }}
                    >
                        Diese Aufstellung wird gerade von{' '}
                        <b>{lineupData.lineup.locker_name} {lineupData.lineup.locker_surname}</b> bearbeitet.
                    </Alert>
                )}

                <Pitch
                    activeFormation={activeFormation}
                    playersOnPitch={playersOnPitch}
                    availablePlayers={availablePlayers}
                    isLocked={isLocked}
                    setSelectedPosForModal={setSelectedPosForModal}
                />

                <LineupControls
                    formations={formations}
                    selectedFormationId={selectedFormationId}
                    setSelectedFormationId={setSelectedFormationId}
                    setIsDirty={setIsDirty}
                    isLocked={isLocked}
                    handleSave={handleSave}
                    setIsManual={setIsManual}
                    isSaving={isSaving}
                    setCopyDialogOpen={setCopyDialogOpen}
                    previousLineups={previousLineups}
                    handleClearLineup={handleClearLineup}
                    playersOnPitch={playersOnPitch}
                />

                <PlayerSelectModal
                    selectedPosForModal={selectedPosForModal}
                    setSelectedPosForModal={setSelectedPosForModal}
                    handlePlayerSelect={handlePlayerSelect}
                    availablePlayers={availablePlayers}
                    activeStats={activeStats}
                />

                <Dialog
                    open={helpDialogOpen}
                    onClose={() => setHelpDialogOpen(false)}
                    maxWidth="sm"
                    fullWidth
                    PaperProps={{
                        sx: {
                            borderRadius: 6,
                            bgcolor: 'rgba(207, 206, 206, 0.95)',
                            backdropFilter: 'blur(20px)',
                            border: '1px solid',
                            borderColor: 'primary.main'
                        }
                    }}
                >
                    <DialogTitle sx={{ fontWeight: 900, fontSize: '1.5rem', color: 'primary.main' }}>HILFE ZUR AUFSTELLUNG</DialogTitle>
                    <DialogContent>
                        <Typography variant="body1" sx={{ fontWeight: 600, mb: 3 }}>
                            Hier kannst du die Aufstellung für dein Team festlegen:
                        </Typography>
                        <Stack spacing={2}>
                            {[
                                { title: 'Formation wählen', text: 'Wähle eine Aufstellung.' },
                                { title: 'Spieler aufstellen', text: 'Klicke auf die Positionen im Spielfeld, um einen Spieler zuzuweisen.' },
                                { title: 'Autosave', text: 'Jede Änderung wird nach 2 Sekunden automatisch als Entwurf gesichert.' },
                                { title: 'Final speichern', text: 'Klicke auf "Final speichern", um die Aufstellung für alle sichtbar zu machen.' }
                            ].map((item, i) => (
                                <Box key={i} sx={{ display: 'flex', gap: 2 }}>
                                    <Typography sx={{ fontWeight: 900, color: 'primary.main' }}>{i + 1}.</Typography>
                                    <Box>
                                        <Typography variant="subtitle2" sx={{ fontWeight: 900, textTransform: 'uppercase' }}>{item.title}</Typography>
                                        <Typography variant="body2" sx={{ color: 'black', fontWeight: 600 }}>{item.text}</Typography>
                                    </Box>
                                </Box>
                            ))}
                        </Stack>
                    </DialogContent>
                    <DialogActions sx={{ p: 3 }}>
                        <Button
                            onClick={() => setHelpDialogOpen(false)}
                            variant="contained"
                            fullWidth
                            sx={{ borderRadius: 3, fontWeight: 900, bgcolor: 'primary.main', color: '#000' }}
                        >
                            Verstanden
                        </Button>
                    </DialogActions>
                </Dialog>

                <Dialog
                    open={copyDialogOpen}
                    onClose={() => setCopyDialogOpen(false)}
                    maxWidth="sm"
                    fullWidth
                    PaperProps={{
                        sx: {
                            borderRadius: 6,
                            bgcolor: 'rgba(20, 20, 20, 0.95)',
                            backdropFilter: 'blur(20px)',
                            border: '1px solid',
                            borderColor: 'divider'
                        }
                    }}
                >
                    <DialogTitle sx={{ fontWeight: 900, fontSize: '1.5rem' }}>FRÜHERE AUFSTELLUNGEN</DialogTitle>
                    <DialogContent>
                        {previousLineups?.lineups?.length ? (
                            <Stack spacing={2} sx={{ mt: 1 }}>
                                {previousLineups.lineups.map(lineup => (
                                    <Box
                                        key={lineup.id}
                                        sx={{
                                            p: 2.5,
                                            border: '1px solid',
                                            borderColor: 'divider',
                                            borderRadius: 4,
                                            cursor: 'pointer',
                                            transition: 'all 0.2s ease',
                                            bgcolor: 'rgba(255,255,255,0.02)',
                                            '&:hover': { borderColor: 'primary.main', bgcolor: 'rgba(190, 242, 100, 0.05)' }
                                        }}
                                        onClick={() => handleCopyPreviousLineup(lineup.id)}
                                    >
                                        <Typography variant="subtitle1" sx={{ fontWeight: 900 }}>
                                            {lineup.termin_name}
                                        </Typography>
                                        <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 800, textTransform: 'uppercase' }}>
                                            {lineup.formation_name} • {new Date(lineup.created_at).toLocaleDateString('de-DE')}
                                        </Typography>
                                    </Box>
                                ))}
                            </Stack>
                        ) : (
                            <Typography sx={{ color: 'text.secondary', fontWeight: 700, textAlign: 'center', py: 4 }}>
                                Keine frühereren Aufstellungen verfügbar
                            </Typography>
                        )}
                    </DialogContent>
                    <DialogActions sx={{ p: 3 }}>
                        <Button onClick={() => setCopyDialogOpen(false)} variant="outlined" sx={{ borderRadius: 3, fontWeight: 900 }}>Abbrechen</Button>
                    </DialogActions>
                </Dialog>

                <ToastAlert />
            </Box>
        </Fade>
    );
};

export default LineupBuilder;